import Logger from '../Logger.js';
import BaseEvent from './BaseEvent.js';
import FeatureDetector from '../FeatureDetector.js';
import MediaStreamBuilder from '../MediaStreamBuilder.js';
import MicMixer from '../MicMixer.js';
import { getScreenAsVideoTracks } from '../utils/StreamHelpers.js';

const adjustMediaStreamTracks = (stream, audio, video) => {
  stream.getAudioTracks().forEach(track => (track.enabled = audio));
  stream.getVideoTracks().forEach(track => (track.enabled = video));
};

/**
 * ChangeLocalStream (optimization after PodiumEvent).
 **/
class ChangeLocalStreamEvent extends BaseEvent {
  handle({ audio, video, screen = false, surface }) {
    Logger.debug('ChangeLocalStreamEvent::handle');
    const { _session } = this.context;
    if (!_session) {
      throw new Error('Session does not exist');
    }
    if (_session.options.recvOnly) {
      return;
    }
    const { localStream, vbgMixer, zoomMixer } = _session;

    if (
      FeatureDetector.isIOSDevice() ||
      FeatureDetector.stopsDeviceActivityIndicatorOnDisable()
    ) {
      if (zoomMixer) {
        if (video) {
          zoomMixer.resume();
        } else {
          zoomMixer.suspend();
        }
        if (
          zoomMixer.originalStream &&
          zoomMixer.originalStream !== localStream
        ) {
          adjustMediaStreamTracks(zoomMixer.originalStream, audio, video);
        }
      }
      if (vbgMixer) {
        if (video) {
          vbgMixer.resume();
        } else {
          vbgMixer.suspend();
        }
        if (
          vbgMixer.originalStream &&
          vbgMixer.originalStream !== localStream
        ) {
          adjustMediaStreamTracks(vbgMixer.originalStream, audio, video);
        }
      }
      adjustMediaStreamTracks(localStream, audio, video);
      localStream.locallyChanged = true;
      _session.emit({ type: 'stream_update', localStream });
      return;
    }

    if (_session.micMixer) {
      _session.micMixer.destroy();
      _session.micMixer = null;
    }
    if (zoomMixer) {
      zoomMixer.stopOriginalStream();
      zoomMixer.terminate();
    }
    if (vbgMixer) {
      vbgMixer.stopOriginalStream();
      vbgMixer.terminate();
    }

    const options = {
      audio: audio,
      video: video,
      zoom: _session.options.zoom,
      virtualBackground: _session.options.virtualBackground,
      existingStream: localStream,
      vbgMixer: vbgMixer,
      zoomMixer: zoomMixer,
      deviceMonitor: _session.deviceMonitor,
      audioPassthrough: _session.options.audioPassthrough,
      scope: _session.options.scope
    };

    if (screen) {
      _session.micMixer = new MicMixer();
      options.video = false;
      options.screen = true;
      options.surface = surface;
      options.virtualBackground = false;
      options.micMixer = _session.micMixer;
    }

    new MediaStreamBuilder(options)
      .start()
      .then(stream => {
        const [screenTrack] = getScreenAsVideoTracks(stream);
        if (screenTrack) {
          screenTrack.addEventListener('ended', () => {
            _session.emit({ type: 'stop_screen_video' });
          });
        }
        return stream;
      })
      .then(stream => {
        stream.locallyChanged = true;
        _session.setStream(stream);
      })
      .catch(error => {
        Logger.error('ChangeLocalStreamEvent', error);
        if (screen) {
          let name = `error_Screen_${error.name}`;
          if (error.message.indexOf('not start audio') > -1) {
            name = `error_Screen_${error.name}_Audio`;
          }
          _session.emit({ type: 'screen_capture_error', name });
          _session.emit({ type: 'stop_screen_video' });
        }
      });
  }
}

export default ChangeLocalStreamEvent;
